import { Box, Paper, Portal, styled, Tooltip, Typography } from "@mui/material";
import { ClimateEffectsFooter } from "components/footer/ClimateEffectsFooter";
import { MeasuresContainer } from "components/measures/climateEffectsPage/MeasuresContainer";
import { PaddedPaper } from "components/surfaces/PaddedPaper";
import { ClimateRiskGraph } from "components/climateRisk/ClimateRiskGraph";
import { ClimateRiskLegend } from "components/climateRisk/ClimateRiskLegend";
import { Map } from "leaflet";
import { useTranslation } from "react-i18next";
import { ClimateRiskLevel, StudyArea } from "types/types";
import { RenderStudyArea } from "components/map/layers/RenderStudyArea";
import { useTopThreeMeasures } from "hooks/measureHooks";
import { Info } from "@mui/icons-material";
import { JoyrideActivator } from "components/joyride/JoyrideActivator";

interface Props {
  mapRef: Map;
  footerRef: HTMLDivElement;
  studyArea: StudyArea;
  uuid: string;
  climateRiskLevels: ClimateRiskLevel[];
}

export function ClimateEffectsDrawerContent(props: Props) {
  const { mapRef, footerRef, studyArea, uuid, climateRiskLevels } = props;
  const topThreeMeasures = useTopThreeMeasures(studyArea.measureImpactList);
  const { t } = useTranslation();
  return (
    <StyledPaper elevation={0} data-tour="step-2">
      <RenderStudyArea
        studyAreaGeo={studyArea.geometry_4326}
        studyAreaBuffer={studyArea.buffer_4326}
        renderBuffer={true}
        mapRef={mapRef}
      />

      <PaddedPaper padding={4} className="climate-risk-container" elevation={0}>
        <JoyrideActivator />
        <Box className="header">
          <Typography variant="h2" component="h1">
            {t("label.climateChallengesInAndAroundMyProject")}
          </Typography>
          <Tooltip title={t("label.climateChallengesInfo")}>
            <Info fontSize="medium" color="primary" />
          </Tooltip>
        </Box>
        <Paper className="climate-risk-graphs" elevation={0}>
          {studyArea.climateRiskList.map((climateRisk) => (
            <ClimateRiskGraph
              key={climateRisk.theme.id}
              climateRisk={climateRisk}
              climateRiskLevels={climateRiskLevels}
            />
          ))}
        </Paper>
        <ClimateRiskLegend climateRiskLevels={climateRiskLevels} />
      </PaddedPaper>
      <MeasuresContainer measures={topThreeMeasures} />
      <Portal container={footerRef}>
        <ClimateEffectsFooter uuid={uuid} />
      </Portal>
    </StyledPaper>
  );
}

const StyledPaper = styled(Paper)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .climate-risk-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    gap: ${({ theme }) => theme.spacing(4)};
  }

  .climate-risk-graphs {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow-y: auto;
    gap: ${({ theme }) => theme.spacing(6)};
  }

  .header {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
  }
`;
